import { Link } from "react-router-dom";
import { Award, Users, ShieldCheck, Heart, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { AnthemVideo } from "@/components/AnthemVideo";

const BENEFITS = [
  {
    icon: Award,
    title: "Your name on every campaign",
    body: "Your name or logo rides on every campaign your Ambassador runs.",
    testid: "benefit-name",
  },
  {
    icon: Users,
    title: "Community recognition",
    body: "Recognition on the MyHeartGives community page, where neighbors gather.",
    testid: "benefit-recognition",
  },
  {
    icon: ShieldCheck,
    title: "Trusted association",
    body: "Association with a trusted, community-led initiative — not another ad.",
    testid: "benefit-trust",
  },
  {
    icon: Heart,
    title: "Real impact",
    body: "The knowledge that you helped activate something real in your community.",
    testid: "benefit-impact",
  },
];

export default function Sponsor() {
  return (
    <div data-testid="sponsor-page">
      {/* HERO */}
      <section className="bg-crimson" data-testid="sponsor-hero">
        <div className="mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 lg:py-28">
          <Reveal>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-gold-bright">
              Sponsorship
            </p>
            <h1
              className="mt-4 font-serif text-4xl font-bold leading-[1.15] tracking-tight text-ivory sm:text-5xl lg:text-6xl"
              data-testid="sponsor-headline"
            >
              Sponsor a Community{" "}
              <span className="italic text-gold-bright">Ambassador</span>
            </h1>
            <p className="mx-auto mt-5 max-w-xl font-serif text-lg italic leading-relaxed text-ivory/85 sm:text-xl" data-testid="sponsor-subheadline">
              Invest in someone already doing the work.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ANTHEM VIDEO — emotional anchor */}
      <AnthemVideo
        testidPrefix="sponsor-anthem"
        eyebrow="See the mission in action"
        headline="This is MyHeartGives"
        subtext="Heart. Story. Community."
        bg="bg-cream"
      />

      {/* WHAT IS AN AMBASSADOR */}
      <section className="mx-auto max-w-3xl px-4 py-20 sm:px-6 lg:py-24" data-testid="sponsor-what-is">
        <Reveal>
          <Eyebrow>The role</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
            What is a Community Ambassador?
          </h2>
          <p className="mt-5 text-base leading-relaxed text-cocoa sm:text-lg">
            A MyHeartGives Ambassador is a local person who knows their
            community — who needs help, who has a story, and where the heart is.
            They connect schools, churches, businesses, families, and local
            organizations around causes that matter. Their first campaign:
            bringing awareness and resources to school lunch programs for
            families in need.
          </p>
        </Reveal>
      </section>

      {/* WHAT DOES A SPONSOR DO */}
      <section className="bg-cream" data-testid="sponsor-what-does">
        <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6 lg:py-24">
          <Reveal>
            <Eyebrow>Your part</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
              What Does a Sponsor Do?
            </h2>
            <p className="mt-5 text-base leading-relaxed text-cocoa sm:text-lg">
              Sponsors cover the cost of the tools that power an Ambassador's
              campaign — digital storytelling, video, AI-assisted outreach,
              email communication, and community media tools. Without sponsors,
              Ambassadors work without the infrastructure they need to succeed.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHAT DOES A SPONSOR GET */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24" data-testid="sponsor-benefits">
        <Reveal>
          <Eyebrow>The return</Eyebrow>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
            What Does a Sponsor Get?
          </h2>
        </Reveal>
        <Reveal delay={0.05}>
          <div
            className="mt-10 rounded-3xl bg-crimson px-8 py-10 text-center shadow-xl sm:px-12"
            data-testid="benefit-headline"
          >
            <p className="font-serif text-xl font-bold italic leading-snug text-ivory sm:text-2xl lg:text-3xl">
              "Your name on everything the Ambassador does for a{" "}
              <span className="text-gold-bright">full year</span>."
            </p>
          </div>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.testid} delay={0.08 * i}>
              <div
                className="h-full rounded-3xl border border-line bg-white p-7 transition-all duration-200 hover:-translate-y-1.5 hover:border-gold/60 hover:shadow-lg"
                data-testid={b.testid}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-crimson-soft text-crimson">
                  <b.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-serif text-lg font-semibold text-ink">
                  {b.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-cocoa">{b.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* THE INVESTMENT */}
      <section className="bg-cream" data-testid="sponsor-investment">
        <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6 lg:py-24">
          <Reveal>
            <Eyebrow className="text-center">The investment</Eyebrow>
            <p className="mt-6 font-serif text-2xl font-bold leading-snug text-ink sm:text-3xl">
              Sponsoring one Ambassador for a full year costs as little as{" "}
              <span className="text-gold" data-testid="sponsor-cost">[SPONSOR COST]</span>
              {" "}— about the price of a business lunch each month.
            </p>
            <p className="mt-5 text-base leading-relaxed text-cocoa sm:text-lg">
              One annual commitment. Twelve months of your name on the work. The
              impact: a community that knows your name for the right reasons.
            </p>
          </Reveal>
        </div>
      </section>

      {/* PULL QUOTE — emotional closer */}
      <section className="bg-crimson" data-testid="sponsor-pull-quote-section">
        <div className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6 lg:py-24">
          <Reveal>
            <p
              className="font-serif text-2xl font-bold leading-relaxed text-gold-bright sm:text-3xl lg:text-4xl"
              data-testid="sponsor-pull-quote"
            >
              <span className="block">You don't have to save the world.</span>
              <span className="mt-4 block">Start with one story.</span>
              <span className="block">One person.</span>
              <span className="block">One family.</span>
              <span className="block">One school.</span>
              <span className="mt-4 block">Find the heart.</span>
              <span className="block italic">We'll help you tell the story.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-3xl px-4 py-20 text-center sm:px-6 lg:py-24" data-testid="sponsor-cta">
        <Reveal>
          <h2 className="font-serif text-2xl font-bold tracking-tight text-ink sm:text-3xl lg:text-4xl">
            Ready to sponsor your community's Ambassador?
          </h2>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-crimson px-10 py-4 text-base font-semibold text-ivory transition-all duration-200 hover:-translate-y-0.5 hover:bg-crimson-deep"
            data-testid="become-sponsor-button"
          >
            Become a Sponsor <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-taupe">
            One-year sponsorship · Renewed only if you love it
          </p>        </Reveal>
      </section>

      {/* PARTNERS */}
      <section className="border-t border-line" data-testid="sponsor-partners">
        <div className="mx-auto max-w-7xl px-4 py-10 text-center sm:px-6 lg:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-taupe" data-testid="partners-line">
            Powered by: <span className="text-cocoa">Wingman AI</span>
            <span className="mx-2 text-gold">|</span>
            <span className="text-cocoa">MemoryPlace</span>
            <span className="mx-2 text-gold">|</span>
            <span className="text-cocoa">GotBackup</span>
          </p>
        </div>
      </section>
    </div>
  );
}
